import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import Slider from 'react-slick'
import Link from 'components/Link'
import currencyUtils from 'utils/currency'
import { CloseIcon, ChevronIcon } from 'components/Icons'
import {
  Body16,
  Title26,
  Body12,
  Body18,
  Title20,
  Eyebrow,
  CtaLabel,
} from 'components/Typography'
import CarouselControls from 'components/Carousel/CarouselControls'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import { isMobileOnly } from 'react-device-detect'
import { useTheme } from 'styled-components'

import theme from 'styles/theme'
import ProductCardPriceInfo from 'components/Card/ProductCard/ProductCardPriceInfo'
import {
  CloseButton,
  ContentWrapper,
  ProductCardList,
  ProductCardListItem,
  HeaderWrapper,
  InnerWrapper,
  ImagePreviewWrapper,
  Overlay,
  Wrapper,
  ContactsWrapper,
  RightWrapper,
  SliderWrapper,
  LeftWrapper,
  BrandLinkWrapper,
  ProductImgContainer,
  ProductImg,
  Logo,
  StyledCarouselControls,
  BottomArrowContainer,
  LTROverride,
  ScrollToBottomComponent,
} from './CarouselItemModal.style'
import { ModalContext } from '../Modal/context'

const CarouselItemModal = (props, ref) => {
  const scrollToBottomRef = useRef(null)
  const [isClosing, setIsClosing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [modalContent, setModalContent] = useState({})
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(true)
  const { setIsModalOpen, isModalOpen } = useContext(ModalContext)
  const themeProps = useTheme()

  useEffect(() => {
    if (isModalOpen) {
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = 'auto'
    }
  }, [isModalOpen])

  const sliderRef = React.useRef(null)

  // Get scroll element Ref to check if is scrollable
  const elRef = useCallback((node) => {
    if (node !== null) {
      const { scrollHeight, clientHeight } = node

      if (scrollHeight > clientHeight) {
        setTimeout(() => {
          setIsScrollAtBottom(false)
        }, 500)
      }
    }
  }, [])

  const handleClose = () => {
    setIsClosing(true)
    setIsScrollAtBottom(true)

    setTimeout(() => {
      setIsClosing(false)
      setIsOpen(false)
      setIsModalOpen(false)
      // @ts-ignore
      window.modalIsOpened = false
    }, 300)
  }

  useImperativeHandle(ref, () => ({
    openModal(data) {
      GA4('product_click', {
        village_name: data.villageName,
        brand_name: data.brand,
        product_name: data.description,
      })
      // Init modal content
      setModalContent({
        brand: data.brand,
        villageName: data.villageName,
        productDescription: data.description,
        longDescription:
          typeof data.longDescription === 'object'
            ? data.longDescription?.longDescription
            : data.longDescription,
        productImages: [data.image],
        productMoreImages: data.moreImages,
        productTitle: data.productTitle,
        brandLink: data.brandLink,
        showBrandLink: data.showBrandLink,
        services: data.services,
        currency: data.currency,
        productModalContactTitle: data.productModalContactTitle,
        contactMethods: data.contactMethods,
        rrpPrice: data.rrpPrice,
        villagePrice: data.villagePrice,
        salePrice: data.salePrice,
        visitBoutiqueLabel: data.visitBoutiqueLabel || 'VISIT THE BOUTIQUE',
        villageLevelLabels: data.villageLevelLabels,
      })
      setIsOpen(true)
      setIsModalOpen(true)
      // @ts-ignore
      window.modalIsOpened = true
    },
  }))

  const onCarouselSwipeChange = useCallback(
    (i) => {
      if (i !== currentSlide) {
        setCurrentSlide(i)
      }
    },
    [currentSlide]
  )

  const onCarouselControlClick = useCallback(
    (i) => {
      setCurrentSlide(i)

      if (sliderRef.current) {
        sliderRef.current.slickGoTo(i)
      }
    },
    [sliderRef]
  )

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight
    if (bottom && !isScrollAtBottom) {
      setIsScrollAtBottom(true)
    } else if (!bottom && isScrollAtBottom) {
      setIsScrollAtBottom(false)
    }
  }

  const settings = {
    dots: true,
    autoplay: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    speed: 500,
    arrows: false,
    adaptiveHeight: true,
    afterChange: onCarouselSwipeChange,
    appendDots: (dots) => <ul>{dots}</ul>,
    customPaging: () => (
      <div className="ft-slick__dots--custom">
        <div className="loading" />
      </div>
    ),
  }

  if (!isOpen) return null

  // @ts-ignore
  const {
    brand,
    productBrandNameOverride,
    productDescription,
    pricingItems,
    productImages,
    currency,
    contactMethods,
    brandLink,
    showBrandLink,
    productMoreImages,
    longDescription,
    visitBoutiqueLabel,
    productModalContactTitle,
    rrpPrice,
    villagePrice,
    salePrice,
    villageLevelLabels,
    villageName,
  } = modalContent

  const {
    colors: { btnMainColor },
  } = useTheme()

  const renderShopContacts = (id) => {
    return (
      <div id={id}>
        <Title20 as="div" py={theme.space[1]} color={btnMainColor}>
          {productModalContactTitle}
        </Title20>
        {contactMethods.map((item) => {
          return (
            <>
              {item.icon && item.label && item.contact ? (
                <ContactsWrapper>
                  <Logo
                    display={['block', null, null]}
                    dangerouslySetInnerHTML={{ __html: item.icon.code.code }}
                  />
                  <Body12
                    as={Link}
                    ml={theme.space[1]}
                    external
                    to={item.contact}
                    textAlign="left"
                    color={btnMainColor}
                    onClick={() => {
                      trackGTM(
                        'Virtual Shopping',
                        brand,
                        productDescription,
                        item.trackLabel || item.label
                      )
                      GA4('product_card_contact_click', {
                        brand_name: brand,
                        contact_type: item.trackLabel || item.label,
                        village_name: villageName,
                      })
                    }}>
                    {item.label.toUpperCase()}
                  </Body12>
                </ContactsWrapper>
              ) : null}
            </>
          )
        })}
        <ScrollToBottomComponent ref={scrollToBottomRef} />
      </div>
    )
  }

  const renderCloseButton = (id) => (
    <CloseButton id={id} type="button" onClick={handleClose}>
      <CloseIcon width={40} color={btnMainColor} />
    </CloseButton>
  )

  // Add productImage and all the productMoreIMages into one array.
  // Map through productMoreImages array to ensure each entry is not undefined.
  const allProductImages = [...productImages]
  if (!!productMoreImages && productMoreImages.length > 0) {
    productMoreImages.map((image) => {
      // Check to ensure image entry is not null or undefined
      if (image != null) {
        allProductImages.push(image)
      }
      return allProductImages
    })
  }

  return (
    <Overlay isClosing={isClosing} isMobileOnly={isMobileOnly}>
      <Wrapper isMobileOnly={isMobileOnly}>
        <ContentWrapper>
          <HeaderWrapper>
            <Body18
              as="div"
              pb={theme.space[1]}
              color={themeProps.colors.sagedarkToblack}
              fontSize={28}>
              {productBrandNameOverride || brand}
            </Body18>
            {renderCloseButton('carousel-item-modal--close-small')}
            {renderCloseButton('carousel-item-modal--close-large')}
          </HeaderWrapper>
          <InnerWrapper
            ref={elRef}
            id="carousel-item-modal--innerWrapper"
            onScroll={handleScroll}>
            <LeftWrapper id="carousel-item-modal--leftWrapper">
              {showBrandLink && (
                <BrandLinkWrapper
                  aria-hidden="true"
                  onClick={() =>
                    trackGTM(
                      'Virtual Shopping',
                      'Virtual Shopping',
                      'product click',
                      brandLink
                    )
                  }>
                  <CtaLabel
                    as={Link}
                    external
                    pb={theme.space[4]}
                    to={brandLink}>
                    {visitBoutiqueLabel}
                  </CtaLabel>
                </BrandLinkWrapper>
              )}

              <Title26
                as="div"
                pb={theme.space[1]}
                color={themeProps.colors.sagedarkToblack}>
                {productDescription}
              </Title26>

              {longDescription?.length > 0 ? (
                <Body16
                  as="div"
                  pb={theme.space[2]}
                  color={theme.colors.sagemedium}
                  style={{ wordWrap: 'break-word', overflowWrap: 'anywhere' }}
                  dangerouslySetInnerHTML={{ __html: longDescription }}
                />
              ) : null}

              <LTROverride>
                <ProductCardPriceInfo
                  {...{
                    currency,
                    rrpPrice,
                    villagePrice,
                    salePrice,
                    labels: villageLevelLabels,
                    columnDirection: 'start',
                    sameSize: true,
                  }}
                />
              </LTROverride>

              {currency && pricingItems && (
                <ProductCardList>
                  {pricingItems.map(({ price, strikethrough }, index) => (
                    <ProductCardListItem
                      strikethrough={strikethrough}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`product-pricing-${index}`}>
                      <Eyebrow mb={[0]}>
                        {currencyUtils[currency](price, { simple: true })}
                      </Eyebrow>
                    </ProductCardListItem>
                  ))}
                </ProductCardList>
              )}

              {contactMethods &&
                contactMethods.length > 0 &&
                renderShopContacts('carousel-item-modal--shop-contacts-large')}
            </LeftWrapper>

            <RightWrapper id="carousel-item-modal--rightWrapper">
              <SliderWrapper>
                <Slider {...settings} ref={sliderRef}>
                  {allProductImages.map((item) => (
                    <ProductImgContainer>
                      <ProductImg
                        image={
                          item?.portrait?.gatsbyImageData ||
                          item?.landscape?.gatsbyImageData
                        }
                        alt="product"
                      />
                    </ProductImgContainer>
                  ))}
                </Slider>
                {allProductImages && allProductImages.length > 1 && (
                  <StyledCarouselControls>
                    <CarouselControls
                      onSlideChange={onCarouselControlClick}
                      currentSlide={currentSlide}
                      totalSlides={allProductImages.length}
                      hasMultipleItemsPerView={false}
                      isBoxed={false}
                      isSmall={false}
                      villageName={villageName}
                    />
                  </StyledCarouselControls>
                )}
              </SliderWrapper>

              <ImagePreviewWrapper id="carousel-item-modal--product-thumbnails" />
            </RightWrapper>
            {contactMethods &&
              contactMethods.length > 0 &&
              renderShopContacts('carousel-item-modal--shop-contacts-small')}
          </InnerWrapper>
        </ContentWrapper>
        {!isScrollAtBottom ? (
          <BottomArrowContainer
            onClick={() =>
              scrollToBottomRef.current.scrollIntoView({ behavior: 'smooth' })
            }>
            <ChevronIcon
              width={20}
              transform="rotate(270deg)"
              pr="45px"
              color={btnMainColor}
            />
          </BottomArrowContainer>
        ) : null}
      </Wrapper>
    </Overlay>
  )
}

export default forwardRef(CarouselItemModal)
